import { html, nothing, svg } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { interpolate, svgTransition } from '../../../visualizations/common/directives/svgTransition.js';
import { getLineSegments } from '../../../visualizations/common/getLineSegments.js';
import { legendItem, barItem } from '../types.js';
import { transitionTiming } from '../animation.js';
import { repeat } from 'lit/directives/repeat.js';
import { translationSet } from '../lang.js';
import { classMap } from 'lit/directives/class-map.js';
import { register as _registerElement } from "../../../elements/one-ux-icon/register-element.js";
_registerElement("icon-70ddba506bf69aa0fcf7936a11e4a05d");
export function Distribution({
  legend,
  bar,
  width,
  implicit,
  translations,
  isMissingData
}: {
  legend: legendItem[];
  bar: barItem;
  width: number;
  implicit: boolean;
  translations: translationSet;
  isMissingData: boolean;
}) {
  const isNonZeroTotal = legend.some(x => !x.exclude && bar.data[x.key] > 0);
  const segments = getLineSegments<legendItem | {
    key: string;
    color: string;
    icon: null;
  }>({
    segments: isNonZeroTotal ? legend.filter(x => !x.exclude).map(x => ({
      item: x,
      value: bar.data[x.key] || 0
    })) : [{
      value: 1,
      item: {
        key: 'zero-total-segment',
        color: 'rgba(var(--one-ux-rgb--brand-neutral-600), 0.12)',
        icon: null
      }
    }],
    width,
    minSegmentWidth: 4,
    gap: 4
  });
  const barHeight = 12;
  const labelHeight = 14;
  const labelWidth = 50;
  const totalHeight = implicit ? barHeight : barHeight + labelHeight;
  if (!width) {
    return html`<div style=${styleMap({
      height: `${totalHeight}px`,
      width: '100%'
    })}></div>`;
  }
  if (isMissingData) {
    const borderWidth = implicit ? 'var(--one-ux-border-width--100)' : 'var(--one-ux-border-width--500)';
    return html`
      <div
        style=${styleMap({
      height: `${totalHeight}px`,
      width: '100%',
      position: 'relative'
    })}
      >
        <div
          class=${classMap({
      'one-ux-typography--label-600': !implicit,
      'one-ux-typography--label-100': implicit
    })}
          style=${styleMap({
      position: 'absolute',
      margin: '0',
      inset: '0',
      padding: '0 var(--one-ux-spacing--large)',
      color: 'var(--one-ux-palette--brand-neutral-400)',
      border: `${borderWidth} solid var(--one-ux-palette--brand-neutral-200)`,
      borderRadius: 'var(--one-ux-radius--200)',
      textAlign: 'center',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      lineHeight: `calc(${totalHeight}px - 2 * ${borderWidth})`
    })}
        >
          ${translations.missingData}
        </div>
      </div>
    `;
  }
  return html`
    <svg
      style=${styleMap({
    height: `${totalHeight}px`,
    width: '100%',
    display: 'block',
    overflow: 'hidden'
  })}
    >
      ${repeat(segments, segment => segment.item.key, segment => {
    let labelX = segment.x + Math.round(segment.width / 2) - Math.round(labelWidth / 2);
    let textAlign = 'center';
    if (labelX < 0) {
      labelX = segment.x;
      textAlign = 'left';
    }
    if (labelX + labelWidth > width) {
      labelX = segment.x + segment.width - labelWidth;
      textAlign = 'right';
    }
    return svg`
          <g
            ${svgTransition({
      timing: transitionTiming,
      attributes: {
        opacity: interpolate.standard(0, 1, 0)
      }
    })}
          >
            <rect
              height=${barHeight}
              fill=${segment.item.color}
              rx="2"
              ${svgTransition({
      timing: transitionTiming,
      attributes: {
        x: interpolate.standard(segment.x, segment.x, segment.x + segment.width / 2),
        width: interpolate.standard(0, segment.width, 0)
      }
    })}
            ></rect>

            ${implicit ? nothing : svg`
                  <foreignObject
                    y=${barHeight}
                    width=${labelWidth}
                    height=${labelHeight}
                    class="one-ux-typography--mono-variant-400"
                    style=${styleMap({
      color: 'var(--one-ux-palette--brand-neutral-600)',
      'text-align': textAlign,
      'word-spacing': '-0.5em'
    })}
                    ${svgTransition({
      timing: transitionTiming,
      attributes: {
        x: interpolate.constant(labelX)
      }
    })}
                  >
                    ${segment.item.icon ? html`
                            <icon-70ddba506bf69aa0fcf7936a11e4a05d
                              icon=${segment.item.icon.name}
                              set=${segment.item.icon.set}
                              style=${styleMap({
      color: segment.item.color,
      'font-size': '12px'
    })}
                            ></icon-70ddba506bf69aa0fcf7936a11e4a05d>
                            ${segment.value}
                          ` : null}
                  </foreignObject>
                `}
          </g>
        `;
  })}
    </svg>
  `;
}